import { buildInformeHTML } from './generateHTML'
import { generatePdfFromHtml } from '../api/informesApi'

const toPeriodo = (dateStr) => {
  if (!dateStr) return ''
  const [year, month] = dateStr.split('-')
  return `${year}${month}`
}

// El popup muestra esta página mientras el PDF se genera.
// Al recibir el postMessage con el ArrayBuffer, crea un blob URL DENTRO del propio
// contexto del popup y lo embebe en un iframe (sin navegar el documento),
// evitando tanto el cross-context blob restriction de Brave como el problema
// de revocación por descarga del documento.
const RECEIVER_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Informe PDF</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { height: 100%; }
    #msg {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
      font-size: 15px;
      color: #555;
      background: #f4f4f4;
    }
    #fr {
      display: none;
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      border: none;
    }
  </style>
</head>
<body>
  <div id="msg">Generando PDF&hellip;</div>
  <iframe id="fr"></iframe>
  <script>
    window.addEventListener('message', function (e) {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== 'PDF_DATA') return;
      try {
        var blob = new Blob([e.data.buffer], { type: 'application/pdf' });
        var url  = URL.createObjectURL(blob);
        var fr   = document.getElementById('fr');
        fr.src   = url;
        fr.style.display = 'block';
        document.getElementById('msg').style.display = 'none';
      } catch (err) {
        document.getElementById('msg').textContent = 'Error al cargar el PDF.';
      }
    });
  </script>
</body>
</html>`

export const generateInformePDF = async (payload, config = {}) => {
  const { targetWindow, ...axiosConfig } = config
  const { reportData } = payload
  const html = buildInformeHTML(payload)

  const periodoDesde = toPeriodo(reportData?.fechaDesde)
  const periodoHasta = toPeriodo(reportData?.fechaHasta)
  let filename = `informe-siniestral-${periodoDesde}-${periodoHasta}.pdf`

  // Inyectar la página receptora en el popup antes del primer await.
  if (targetWindow) {
    targetWindow.document.open()
    targetWindow.document.write(RECEIVER_HTML)
    targetWindow.document.close()
  }

  const response = await generatePdfFromHtml(html, axiosConfig)

  // Intentar tomar el nombre del archivo del header Content-Disposition
  const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']
  if (cd) {
    const matchStar = /filename\*=UTF-8''([^;\n]+)/i.exec(cd)
    const matchSimple = /filename="?([^";\n]+)"?/i.exec(cd)
    if (matchStar) {
      try {
        filename = decodeURIComponent(matchStar[1])
      } catch {
        filename = matchStar[1]
      }
    } else if (matchSimple) {
      filename = matchSimple[1]
    }
  }

  const contentType = response.headers?.['content-type'] || ''

  if (contentType.includes('application/pdf')) {
    // Validar magic bytes: %PDF = 0x25 0x50 0x44 0x46
    const view = new Uint8Array(response.data)
    if (view.length < 4 || view[0] !== 0x25 || view[1] !== 0x50 || view[2] !== 0x44 || view[3] !== 0x46) {
      throw new Error(`El servidor devolvió datos inválidos (no es un PDF, ${view.length} bytes)`)
    }

    if (targetWindow) {
      // Transferir el ArrayBuffer al popup (zero-copy).
      // El popup crea su propio blob URL en su contexto y lo embebe en el iframe.
      targetWindow.postMessage({ type: 'PDF_DATA', buffer: response.data }, window.location.origin, [response.data])
      return
    }

    // Fallback: descarga directa
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    return
  }

  // No es PDF — decodificar error desde el ArrayBuffer
  const decoder = new TextDecoder('utf-8')
  const text = decoder.decode(response.data)
  let message = text
  try {
    const json = JSON.parse(text)
    message = json?.error?.message || json?.message || text
  } catch {
    // No es JSON válido: se conserva el mensaje plano original
  }
  throw new Error(message)
}
